import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatLongDate, saTodayISO } from '../lib/dateUtils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Plus, LogOut, MapPin, Users, Play, Square, UserPlus, UserMinus, User, MessageSquare, Mail, MailOpen, Copy, Check, Eye, HeartPulse, Trash2, PhoneCall as PhoneCallIcon } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import MobileActionMenu from '../components/MobileActionMenu';
import { SubscriptionBadge } from '../components/SubscriptionBadge';
import { FinancePromo } from '../components/finance/FinancePromo';
import { MyReviewsDialog } from '../components/reviews/MyReviewsDialog';
import { Star, Wallet } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phone validation helper
const formatSAPhone = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Handle different starting formats
  let normalized = digits;
  if (digits.startsWith('27')) {
    normalized = digits.slice(2);
  } else if (digits.startsWith('0')) {
    normalized = digits.slice(1);
  }
  
  // Limit to 9 digits after country code
  normalized = normalized.slice(0, 9);
  
  // Format as +27 XX XXX XXXX
  if (normalized.length > 0) {
    let formatted = '+27 ';
    if (normalized.length > 0) formatted += normalized.slice(0, 2);
    if (normalized.length > 2) formatted += ' ' + normalized.slice(2, 5);
    if (normalized.length > 5) formatted += ' ' + normalized.slice(5, 9);
    return formatted;
  }
  return value;
};

const validateSAPhone = (phone) => {
  const digits = phone.replace(/\D/g, '');
  // Should have 11 digits total (+27 + 9 digits) or 10 digits (0 + 9 digits)
  return digits.length === 11 || digits.length === 9;
};

export default function DriverDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [showReviews, setShowReviews] = useState(false);
  const [trips, setTrips] = useState([]);
  const [schools, setSchools] = useState([]);
  const [kids, setKids] = useState([]);
  const [driverInfo, setDriverInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showCreateTrip, setShowCreateTrip] = useState(false);
  const [showAddKid, setShowAddKid] = useState(false);
  const [showAddKidOnly, setShowAddKidOnly] = useState(false);
  const [showArchiveKid, setShowArchiveKid] = useState(false);
  const [kidToArchive, setKidToArchive] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [archivedKids, setArchivedKids] = useState([]);
  const [archiveToRemove, setArchiveToRemove] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [messages, setMessages] = useState([]);
  const [lastAddedKid, setLastAddedKid] = useState(null);
  const [copiedKidId, setCopiedKidId] = useState(false);
  const [viewKid, setViewKid] = useState(null);
  const [viewTrip, setViewTrip] = useState(null);
  const [pendingLinks, setPendingLinks] = useState([]);
  const [rejectLinkKid, setRejectLinkKid] = useState(null);
  const [linkRejectionReason, setLinkRejectionReason] = useState('');
  
  // Form states
  const [newTrip, setNewTrip] = useState({
    school_id: '',
    route_name: '',
    trip_type: 'morning',
    term: '',
    start_date: '',
    end_date: '',
    scheduled_time: '',
    kid_ids: []
  });
  const [newKid, setNewKid] = useState({
    full_name: '',
    grade: '',
    parent_email: '',
    parent_name: '',
    parent_phone: '',
    pickup_address: '',
    school_id: ''
  });
  const [newKidOnly, setNewKidOnly] = useState({
    full_name: '',
    grade: '',
    pickup_address: '',
    drop_off_address: '',
    school_id: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, tripsRes, driversRes, schoolsRes, kidsRes, messagesRes, pendingLinksRes, absencesRes, archiveRes] = await Promise.all([
        axios.get(`${API_URL}/stats/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/trips`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/drivers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/schools`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/driver/kids`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/hierarchy/messages/inbox`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/kids/pending-links`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/driver/absences`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/driver/archive`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setAbsences(absencesRes.data || []);
      setArchivedKids(archiveRes.data || []);
      setStats(statsRes.data);
      setTrips(tripsRes.data);
      const myDriver = driversRes.data.find(d => d.user_id === user.user_id);
      setDriverInfo(myDriver);
      setSchools(schoolsRes.data);
      setKids(kidsRes.data);
      setMessages(messagesRes.data || []);
      setPendingLinks(pendingLinksRes.data || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token, user.user_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateTrip = async () => {
    if (!newTrip.school_id || !newTrip.route_name || !newTrip.term || !newTrip.start_date || !newTrip.end_date || !newTrip.scheduled_time) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API_URL}/trips`, newTrip, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trip created successfully!');
      setShowCreateTrip(false);
      setNewTrip({ school_id: '', route_name: '', term: '', start_date: '', end_date: '', scheduled_time: '', kid_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create trip');
    }
  };

  const handleAddKid = async () => {
    if (!newKid.full_name || !newKid.grade || !newKid.parent_email || !newKid.parent_name || !newKid.parent_phone || !newKid.pickup_address || !newKid.school_id) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!validateSAPhone(newKid.parent_phone)) {
      toast.error('Please enter a valid South African phone number (e.g., +27 82 123 4567)');
      return;
    }
    try {
      await axios.post(`${API_URL}/driver/add-parent-and-kid`, newKid, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Parent and kid added successfully!');
      setShowAddKid(false);
      setNewKid({ full_name: '', grade: '', parent_email: '', parent_name: '', parent_phone: '', pickup_address: '', school_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add parent and kid');
    }
  };

  const handleStartTrip = async (tripId) => {
    try {
      await axios.put(`${API_URL}/trips/${tripId}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trip started! Parents have been notified.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trip');
    }
  };

  const handleEndTrip = async (tripId) => {
    try {
      await axios.put(`${API_URL}/trips/${tripId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trip ended! Parents have been notified.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end trip');
    }
  };

  const handleApproveLink = async (kid) => {
    try {
      await axios.post(`${API_URL}/kids/${kid.kid_id}/approve-link`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Link approved for ${kid.full_name}. Parent has been notified.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve link');
    }
  };

  const handleRejectLink = async () => {
    if (!rejectLinkKid) return;
    try {
      await axios.post(`${API_URL}/kids/${rejectLinkKid.kid_id}/reject-link`,
        { reason: linkRejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Link rejected for ${rejectLinkKid.full_name}. Parent has been notified.`);
      setRejectLinkKid(null);
      setLinkRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject link');
    }
  };

  const handleArchiveKid = async () => {
    if (!archiveReason.trim()) {
      toast.error('Please provide a reason for archiving');
      return;
    }
    try {
      await axios.put(`${API_URL}/driver/kids/${kidToArchive.kid_id}/archive`, 
        { reason: archiveReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kid archived. Parent and transport operator have been notified.');
      setShowArchiveKid(false);
      setKidToArchive(null);
      setArchiveReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive kid');
    }
  };

  const handleRemoveFromArchive = async () => {
    try {
      await axios.delete(`${API_URL}/driver/archive/${archiveToRemove.kid_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Removed from your archive.');
      setArchiveToRemove(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove item');
    }
  };

  const handleAddKidOnly = async () => {
    if (!newKidOnly.full_name || !newKidOnly.grade || !newKidOnly.pickup_address || !newKidOnly.school_id) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/driver/add-kid`, newKidOnly, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastAddedKid(response.data);
      toast.success('Kid added! Share the Kid ID with the parent for linking.');
      setNewKidOnly({ full_name: '', grade: '', pickup_address: '', drop_off_address: '', school_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add kid');
    }
  };

  const handleMarkMessageRead = async (messageId) => {
    try {
      await axios.put(
        `${API_URL}/hierarchy/messages/${messageId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(messages.map(m => m.message_id === messageId ? { ...m, read: true } : m));
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  };

  const toggleKidSelection = (kidId) => {
    const currentSelection = newTrip.kid_ids || [];
    if (currentSelection.includes(kidId)) {
      setNewTrip({ ...newTrip, kid_ids: currentSelection.filter(id => id !== kidId) });
    } else {
      setNewTrip({ ...newTrip, kid_ids: [...currentSelection, kidId] });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const activeTrips = trips.filter(t => t.status === 'active' && !t.is_currently_active);
  const inProgressTrips = trips.filter(t => t.is_currently_active);
  const completedSessions = trips.reduce((acc, t) => acc + (t.sessions?.filter(s => s.status === 'completed').length || 0), 0);
  const unreadMessages = messages.filter(m => !m.read).length;
  const todayISO = saTodayISO();
  const absentTodayIds = new Set(absences.filter(a => a.date === todayISO).map(a => a.kid_id));
  const kidName = (id) => kids.find(k => k.kid_id === id)?.full_name || 'Learner';

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white" data-testid="driver-dashboard">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <img src="/ntwanayam-logo.jpg" alt="NtwanaYam Logo" className="w-12 h-12 rounded-xl object-cover shadow-sm hidden sm:block" data-testid="brand-logo" />
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold text-foreground" data-testid="dashboard-title">Driver Dashboard</h1>
              <p className="text-sm text-muted-foreground" data-testid="user-name">Welcome, {user.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <MobileActionMenu title="Menu">
            <Button variant="default" onClick={() => setShowAddKid(true)} className="rounded-full" data-testid="add-kid-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Add Parent & Kid
            </Button>
            <Button variant="outline" onClick={() => navigate('/finance')} className="rounded-full text-emerald-700 border-emerald-200" data-testid="finance-nav-button">
              <Wallet className="w-4 h-4 mr-2" /> Finance
            </Button>
            <Button variant="outline" onClick={() => setShowReviews(true)} className="rounded-full text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="write-review-button">
              <Star className="w-4 h-4 mr-2" /> Write Review
            </Button>
            <Button variant="outline" onClick={() => navigate('/profile')} className="rounded-full" data-testid="profile-button">
              <User className="w-4 h-4 mr-2" /> Profile
            </Button>
            <Button variant="outline" onClick={logout} className="rounded-full" data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
            </MobileActionMenu>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <SubscriptionBadge token={token} />
        <FinancePromo token={token} />
        {/* Driver Status */}
        {driverInfo && (
          <Card className="mb-8 shadow-lg" data-testid="driver-status-card">
            <CardHeader>
              <CardTitle className="text-2xl font-heading">Driver Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                {driverInfo.status === 'active' && driverInfo.vehicle_id ? (
                  <>
                    <CheckCircle className="w-12 h-12 text-emerald-500" />
                    <div>
                      <p className="text-lg font-semibold text-emerald-600" data-testid="driver-status-text">Operational</p>
                      <p className="text-sm text-muted-foreground">Vehicle assigned - ready for trips</p>
                    </div>
                  </>
                ) : driverInfo.status === 'active' && !driverInfo.vehicle_id ? (
                  <>
                    <AlertCircle className="w-12 h-12 text-amber-500" />
                    <div>
                      <p className="text-lg font-semibold text-amber-600" data-testid="driver-status-text">No Vehicle Assigned</p>
                      <p className="text-sm text-muted-foreground">Contact your transport operator to assign a vehicle</p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-12 h-12 text-blue-500" />
                    <div>
                      <p className="text-lg font-semibold text-blue-600" data-testid="driver-status-text">Pending Approval</p>
                      <p className="text-sm text-muted-foreground">Awaiting administrator approval</p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-testid="stats-grid">
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Active Trips</CardTitle>
              <MapPin className="h-5 w-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{activeTrips.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Ready to start</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">In Progress</CardTitle>
              <Play className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{inProgressTrips.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Currently running</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Completed Sessions</CardTitle>
              <CheckCircle className="h-5 w-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{completedSessions}</div>
              <p className="text-xs text-muted-foreground mt-1">Total completed</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Kids Registered</CardTitle>
              <Users className="h-5 w-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{kids.length}</div>
              <p className="text-xs text-muted-foreground mt-1">In your care</p>
            </CardContent>
          </Card>
        </div>

        {/* Trips Section */}
        <Card className="shadow-lg mb-8" data-testid="trips-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-heading">My Trips</CardTitle>
              <CardDescription>Create and manage your term trips</CardDescription>
            </div>
            <Dialog open={showCreateTrip} onOpenChange={setShowCreateTrip}>
              <DialogTrigger asChild>
                <Button className="rounded-full" disabled={!driverInfo?.vehicle_id} data-testid="create-trip-button">
                  <Plus className="w-4 h-4 mr-2" /> Create Trip
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-trip-dialog">
                <DialogHeader>
                  <DialogTitle>Create Term Trip</DialogTitle>
                  <DialogDescription>Create a new trip for the school term (no approval needed)</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>School *</Label>
                    <Select onValueChange={(value) => setNewTrip({ ...newTrip, school_id: value })}>
                      <SelectTrigger className="h-12" data-testid="trip-school-select">
                        <SelectValue placeholder="Select school" />
                      </SelectTrigger>
                      <SelectContent>
                        {schools.map(school => (
                          <SelectItem key={school.school_id} value={school.school_id}>{school.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Route Name *</Label>
                    <Input value={newTrip.route_name} onChange={(e) => setNewTrip({ ...newTrip, route_name: e.target.value })} placeholder="e.g., Morning Route A" data-testid="trip-route-input" />
                  </div>
                  <div>
                    <Label>Trip Type *</Label>
                    <Select value={newTrip.trip_type} onValueChange={(value) => setNewTrip({ ...newTrip, trip_type: value })}>
                      <SelectTrigger className="h-12" data-testid="trip-type-select">
                        <SelectValue placeholder="Select trip type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="morning">Morning (Home → School)</SelectItem>
                        <SelectItem value="afternoon">Afternoon (School → Home)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Term *</Label>
                    <Select onValueChange={(value) => setNewTrip({ ...newTrip, term: value })}>
                      <SelectTrigger className="h-12" data-testid="trip-term-select">
                        <SelectValue placeholder="Select term" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Term 1">Term 1</SelectItem>
                        <SelectItem value="Term 2">Term 2</SelectItem>
                        <SelectItem value="Term 3">Term 3</SelectItem>
                        <SelectItem value="Term 4">Term 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date *</Label>
                      <Input type="date" value={newTrip.start_date} onChange={(e) => setNewTrip({ ...newTrip, start_date: e.target.value })} data-testid="trip-start-date" />
                    </div>
                    <div>
                      <Label>End Date *</Label>
                      <Input type="date" value={newTrip.end_date} onChange={(e) => setNewTrip({ ...newTrip, end_date: e.target.value })} data-testid="trip-end-date" />
                    </div>
                  </div>
                  <div>
                    <Label>Daily Time *</Label>
                    <Input type="time" value={newTrip.scheduled_time} onChange={(e) => setNewTrip({ ...newTrip, scheduled_time: e.target.value })} data-testid="trip-time" />
                  </div>
                  <div>
                    <Label>Load Kids Into Trip</Label>
                    <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                      {kids.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No kids registered yet. Add kids first.</p>
                      ) : (
                        kids.map(kid => (
                          <div key={kid.kid_id} className="flex items-center space-x-3 py-2 border-b last:border-0">
                            <Checkbox id={kid.kid_id} checked={(newTrip.kid_ids || []).includes(kid.kid_id)} onCheckedChange={() => toggleKidSelection(kid.kid_id)} />
                            <label htmlFor={kid.kid_id} className="text-sm cursor-pointer flex-1">
                              <span className="font-medium">{kid.full_name}</span>
                              <span className="text-muted-foreground ml-2">({kid.school_name}, {kid.grade})</span>
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{(newTrip.kid_ids || []).length} kid(s) selected</p>
                  </div>
                </div>
                <DialogFooter className="mt-4">
                  <Button variant="outline" onClick={() => setShowCreateTrip(false)}>Cancel</Button>
                  <Button onClick={handleCreateTrip} data-testid="submit-trip-btn">Create Trip</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {trips.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-trips">
                <MapPin className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                <p>No trips yet. Create your first term trip!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {trips.map((trip) => (
                  <div key={trip.trip_id} className="p-4 border rounded-xl hover:shadow-md transition-all" data-testid={`trip-item-${trip.trip_id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-lg">{trip.route_name}</h3>
                          <Badge variant="outline">{trip.term}</Badge>
                          <Badge className={trip.is_currently_active ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                            {trip.is_currently_active ? 'In Progress' : trip.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {trip.school_name} | {trip.start_date} to {trip.end_date} at {trip.scheduled_time}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {(trip.kid_ids?.length || 0) - (trip.absent_today?.length || 0)} of {trip.kid_ids?.length || 0} kid(s) riding today | {trip.sessions?.length || 0} session(s) completed
                        </p>
                        {trip.absent_today?.length > 0 && (
                          <div className="mt-2 flex flex-wrap items-center gap-1" data-testid={`trip-absent-${trip.trip_id}`}>
                            <span className="text-xs font-semibold text-amber-800">Absent today — don't wait:</span>
                            {trip.absent_today.map(id => (
                              <Badge key={id} className="bg-amber-100 text-amber-800 border border-amber-300 text-xs">{kidName(id)}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewTrip(trip)} data-testid={`view-trip-${trip.trip_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {trip.status === 'active' && !trip.is_currently_active && (
                          <Button size="sm" className="rounded-full bg-emerald-500 hover:bg-emerald-600" onClick={() => handleStartTrip(trip.trip_id)} data-testid={`start-trip-${trip.trip_id}`}>
                            <Play className="w-4 h-4 mr-1" /> Start Trip
                          </Button>
                        )}
                        {trip.is_currently_active && (
                          <Button size="sm" variant="destructive" className="rounded-full" onClick={() => handleEndTrip(trip.trip_id)} data-testid={`end-trip-${trip.trip_id}`}>
                            <Square className="w-4 h-4 mr-1" /> End Trip
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Kids Section */}
        <Card className="shadow-lg mb-8" data-testid="kids-section">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">My Kids</CardTitle>
            <CardDescription>Children assigned to your care</CardDescription>
          </CardHeader>
          <CardContent>
            {kids.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No kids registered yet</p>
                <Button variant="outline" className="mt-4 rounded-full" onClick={() => setShowAddKid(true)}>
                  <UserPlus className="w-4 h-4 mr-2" /> Add Parent & Kid
                </Button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {kids.map((kid) => {
                  const hasMedical = (kid.medical_alerts && kid.medical_alerts.length > 0) || kid.medical_notes;
                  const contacts = kid.emergency_contacts || [];
                  return (
                  <div key={kid.kid_id} className={`p-4 border rounded-xl hover:shadow-md transition-all ${hasMedical ? 'border-red-300' : ''}`} data-testid={`kid-card-${kid.kid_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-lg">{kid.full_name}</h3>
                          {absentTodayIds.has(kid.kid_id) && (
                            <Badge className="bg-amber-500 text-white" data-testid={`kid-absent-today-${kid.kid_id}`}>Absent today</Badge>
                          )}
                          {hasMedical && (
                            <Badge className="bg-red-600 text-white gap-1" data-testid={`kid-medical-badge-${kid.kid_id}`}>
                              <HeartPulse className="w-3 h-3" /> Medical
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{kid.grade}</p>
                        <p className="text-sm text-muted-foreground">{kid.school_name}</p>
                        <p className="text-xs text-muted-foreground mt-2">Parent: {kid.parent_name || 'Not linked'}</p>
                        <p className="text-xs text-muted-foreground">Pickup: {kid.pickup_address}</p>
                        {absences.some(a => a.kid_id === kid.kid_id && a.date !== todayISO) && (
                          <p className="text-xs text-amber-700 mt-1" data-testid={`kid-upcoming-absences-${kid.kid_id}`}>
                            Upcoming absences: {absences.filter(a => a.kid_id === kid.kid_id && a.date !== todayISO).map(a => a.date).join(', ')}
                          </p>
                        )}
                        {hasMedical && (
                          <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2" data-testid={`medical-inline-${kid.kid_id}`}>
                            <div className="flex items-center gap-1 text-red-800 font-semibold text-xs mb-1">
                              <HeartPulse className="w-3.5 h-3.5" /> Medical alerts
                            </div>
                            {kid.medical_alerts?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-1">
                                {kid.medical_alerts.map((a) => (
                                  <span key={a} className="text-xs bg-red-100 text-red-800 border border-red-200 px-1.5 py-0.5 rounded">{a}</span>
                                ))}
                              </div>
                            )}
                            {kid.medical_notes && (
                              <p className="text-xs text-red-900 whitespace-pre-wrap">{kid.medical_notes}</p>
                            )}
                          </div>
                        )}
                        {contacts.length > 0 && (
                          <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-2" data-testid={`emergency-inline-${kid.kid_id}`}>
                            <div className="flex items-center gap-1 text-blue-800 font-semibold text-xs mb-1">
                              <PhoneCallIcon className="w-3.5 h-3.5" /> Emergency contacts
                            </div>
                            <ul className="space-y-0.5">
                              {contacts.map((c, i) => (
                                <li key={i} className="text-xs text-blue-900">
                                  {c.name || 'Unnamed'}{c.relationship ? ` (${c.relationship})` : ''}
                                  {c.phone && (
                                    <> — <a href={`tel:${c.phone}`} className="underline font-medium">{c.phone}</a></>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="rounded-full"
                          onClick={() => setViewKid(kid)}
                          data-testid={`view-kid-${kid.kid_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="rounded-full text-red-600 hover:bg-red-50"
                          onClick={() => { setKidToArchive(kid); setShowArchiveKid(true); }}
                          data-testid={`archive-kid-${kid.kid_id}`}
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Archive Section */}
        <Card className="shadow-lg mb-8" data-testid="driver-archive-section">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">My Archive</CardTitle>
            <CardDescription>Children you have removed from your list. Removing them here only hides them from you.</CardDescription>
          </CardHeader>
          <CardContent>
            {archivedKids.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="driver-archive-empty">Your archive is empty.</p>
            ) : (
              <div className="space-y-2">
                {archivedKids.map((kid) => (
                  <div key={kid.kid_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 border rounded-xl bg-slate-50" data-testid={`archive-item-${kid.kid_id}`}>
                    <div className="min-w-0">
                      <p className="font-medium">{kid.full_name} <span className="text-xs text-muted-foreground">({kid.grade})</span></p>
                      <p className="text-xs text-muted-foreground">Archived {kid.archived_at ? formatLongDate(kid.archived_at) : ''}{kid.removal_reason ? ` — ${kid.removal_reason}` : ''}</p>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:bg-red-50" onClick={() => setArchiveToRemove(kid)} data-testid={`archive-delete-${kid.kid_id}`}>
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!archiveToRemove} onOpenChange={(o) => { if (!o) setArchiveToRemove(null); }}>
          <DialogContent data-testid="archive-delete-dialog">
            <DialogHeader>
              <DialogTitle>Delete from your archive?</DialogTitle>
              <DialogDescription>{archiveToRemove?.full_name} will no longer show in your archive. Admins keep the record.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setArchiveToRemove(null)}>Cancel</Button>
              <Button variant="destructive" onClick={handleRemoveFromArchive} data-testid="archive-delete-confirm">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Complaints Link */}
        <Card className="shadow-lg mb-8">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Complaints</CardTitle>
            <CardDescription>View and respond to complaints from parents</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="rounded-full" onClick={() => navigate('/complaints')} data-testid="view-complaints-btn">
              <MessageSquare className="w-4 h-4 mr-2" /> View Complaints
            </Button>
          </CardContent>
        </Card>

        {/* Messages Section */}
        <Card className="shadow-lg mb-8" data-testid="messages-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-heading">
                Messages
                {unreadMessages > 0 && (
                  <Badge className="ml-2 bg-red-500">{unreadMessages} new</Badge>
                )}
              </CardTitle>
              <CardDescription>Broadcast messages from administrators and transport operators</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="no-messages">
                  <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No messages yet</p>
                </div>
              ) : (
                messages.map((message) => (
                  <div 
                    key={message.message_id} 
                    className={`p-4 border rounded-xl hover:shadow-md transition-all cursor-pointer ${!message.read ? 'bg-blue-50 border-blue-200' : ''}`} 
                    data-testid={`message-item-${message.message_id}`}
                    onClick={() => !message.read && handleMarkMessageRead(message.message_id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {message.read ? (
                          <MailOpen className="w-5 h-5 text-gray-400 mt-1" />
                        ) : (
                          <Mail className="w-5 h-5 text-blue-500 mt-1" />
                        )}
                        <div>
                          <h3 className="font-semibold" data-testid={`message-subject-${message.message_id}`}>
                            {message.subject || 'Broadcast Message'}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1" data-testid={`message-content-${message.message_id}`}>
                            {message.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            From: {message.sender_name || 'Administrator'} • {formatDate(message.created_at)}
                          </p>
                        </div>
                      </div>
                      {!message.read && (
                        <Badge variant="default" className="bg-blue-500">New</Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </main>
      <MyReviewsDialog open={showReviews} onOpenChange={setShowReviews} token={token} />

      {/* Add Kid Dialog */}
      <Dialog open={showAddKid} onOpenChange={setShowAddKid}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-kid-dialog">
          <DialogHeader>
            <DialogTitle>Add Parent & Kid</DialogTitle>
            <DialogDescription>Register a new parent and their child (no approval needed)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-b pb-4">
              <h4 className="font-medium mb-3">Kid Information</h4>
              <div className="space-y-3">
                <div>
                  <Label>Kid&apos;s Full Name *</Label>
                  <Input value={newKid.full_name} onChange={(e) => setNewKid({ ...newKid, full_name: e.target.value })} placeholder="e.g., Thabo Mokoena" data-testid="kid-name-input" />
                </div>
                <div>
                  <Label>Grade *</Label>
                  <Input value={newKid.grade} onChange={(e) => setNewKid({ ...newKid, grade: e.target.value })} placeholder="e.g., Grade 5" data-testid="kid-grade-input" />
                </div>
                <div>
                  <Label>School *</Label>
                  <Select onValueChange={(value) => setNewKid({ ...newKid, school_id: value })}>
                    <SelectTrigger data-testid="kid-school-select">
                      <SelectValue placeholder="Select school" />
                    </SelectTrigger>
                    <SelectContent>
                      {schools.map(school => (
                        <SelectItem key={school.school_id} value={school.school_id}>{school.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pickup Address *</Label>
                  <Input value={newKid.pickup_address} onChange={(e) => setNewKid({ ...newKid, pickup_address: e.target.value })} placeholder="e.g., 123 Main St, Soweto" data-testid="kid-address-input" />
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">Parent Information</h4>
              <div className="space-y-3">
                <div>
                  <Label>Parent&apos;s Full Name *</Label>
                  <Input value={newKid.parent_name} onChange={(e) => setNewKid({ ...newKid, parent_name: e.target.value })} placeholder="e.g., Nomsa Mokoena" data-testid="parent-name-input" />
                </div>
                <div>
                  <Label>Parent&apos;s Email *</Label>
                  <Input type="email" value={newKid.parent_email} onChange={(e) => setNewKid({ ...newKid, parent_email: e.target.value })} placeholder="e.g., parent@example.com" data-testid="parent-email-input" />
                </div>
                <div>
                  <Label>Parent&apos;s Phone *</Label>
                  <Input 
                    value={newKid.parent_phone} 
                    onChange={(e) => setNewKid({ ...newKid, parent_phone: formatSAPhone(e.target.value) })} 
                    placeholder="+27 82 123 4567" 
                    data-testid="parent-phone-input" 
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAddKid(false)}>Cancel</Button>
            <Button onClick={handleAddKid} data-testid="submit-kid-btn">Add Parent & Kid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Kid Dialog */}
      <Dialog open={showArchiveKid} onOpenChange={setShowArchiveKid}>
        <DialogContent data-testid="archive-kid-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Archive Child</DialogTitle>
            <DialogDescription>
              Remove {kidToArchive?.full_name} from your service. The parent will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
              <AlertCircle className="w-4 h-4 inline-block mr-2" />
              This action will notify the parent and transport operator.
            </div>
            <div>
              <Label>Reason for archiving *</Label>
              <Textarea 
                value={archiveReason} 
                onChange={(e) => setArchiveReason(e.target.value)} 
                placeholder="e.g., Child moved to different school, service no longer required..."
                rows={3}
                data-testid="archive-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowArchiveKid(false); setArchiveReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleArchiveKid} data-testid="confirm-archive-kid-btn">
              <UserMinus className="w-4 h-4 mr-2" /> Archive Child
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Kid Only Dialog */}
      <Dialog open={showAddKidOnly} onOpenChange={setShowAddKidOnly}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-kid-only-dialog">
          <DialogHeader>
            <DialogTitle>Add Kid (Parent Links Later)</DialogTitle>
            <DialogDescription>
              Add a child to your vehicle. You&apos;ll receive a Kid ID to share with the parent for linking.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {lastAddedKid && (
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2">Last Added Kid</h4>
                <p className="text-sm text-green-700"><strong>Name:</strong> {lastAddedKid.kid_name}</p>
                <p className="text-sm text-green-700"><strong>Kid ID:</strong></p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 bg-green-100 p-2 rounded text-xs break-all">{lastAddedKid.kid_id}</code>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className={copiedKidId ? "bg-green-500 text-white" : ""}
                    onClick={() => {
                      navigator.clipboard.writeText(lastAddedKid.kid_id);
                      setCopiedKidId(true);
                      toast.success('Kid ID copied to clipboard!');
                      setTimeout(() => setCopiedKidId(false), 2000);
                    }}
                    data-testid="copy-kid-id-btn"
                  >
                    {copiedKidId ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                {lastAddedKid.vehicle_registration && (
                  <p className="text-sm text-green-700 mt-2"><strong>Vehicle:</strong> {lastAddedKid.vehicle_registration}</p>
                )}
                <p className="text-xs text-green-600 mt-2">Share this Kid ID with the parent so they can link their account.</p>
              </div>
            )}
            <div className="space-y-3">
              <div>
                <Label>Kid&apos;s Full Name *</Label>
                <Input 
                  value={newKidOnly.full_name} 
                  onChange={(e) => setNewKidOnly({ ...newKidOnly, full_name: e.target.value })} 
                  placeholder="e.g., Thabo Mokoena" 
                  data-testid="kid-only-name-input" 
                />
              </div>
              <div>
                <Label>Grade *</Label>
                <Input 
                  value={newKidOnly.grade} 
                  onChange={(e) => setNewKidOnly({ ...newKidOnly, grade: e.target.value })} 
                  placeholder="e.g., Grade 5" 
                  data-testid="kid-only-grade-input" 
                />
              </div>
              <div>
                <Label>School *</Label>
                <Select onValueChange={(value) => setNewKidOnly({ ...newKidOnly, school_id: value })}>
                  <SelectTrigger data-testid="kid-only-school-select">
                    <SelectValue placeholder="Select school" />
                  </SelectTrigger>
                  <SelectContent>
                    {schools.map(school => (
                      <SelectItem key={school.school_id} value={school.school_id}>{school.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Home Pickup Address (Morning) *</Label>
                <Input 
                  value={newKidOnly.pickup_address} 
                  onChange={(e) => setNewKidOnly({ ...newKidOnly, pickup_address: e.target.value })} 
                  placeholder="e.g., 123 Main St, Soweto" 
                  data-testid="kid-only-pickup-input" 
                />
              </div>
              <div>
                <Label>Home Drop-off Address (Afternoon)</Label>
                <Input 
                  value={newKidOnly.drop_off_address} 
                  onChange={(e) => setNewKidOnly({ ...newKidOnly, drop_off_address: e.target.value })} 
                  placeholder="Same as pickup if blank" 
                  data-testid="kid-only-dropoff-input" 
                />
                <p className="text-xs text-muted-foreground mt-1">Leave blank if same as pickup address</p>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowAddKidOnly(false); setLastAddedKid(null); }}>Close</Button>
            <Button onClick={handleAddKidOnly} data-testid="submit-kid-only-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Add Kid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Parent Link Dialog */}
      <Dialog open={!!rejectLinkKid} onOpenChange={(open) => { if (!open) { setRejectLinkKid(null); setLinkRejectionReason(''); } }}>
        <DialogContent data-testid="reject-link-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Reject Parent Link</DialogTitle>
            <DialogDescription>
              Reject the link request from <strong>{rejectLinkKid?.parent_name}</strong> to <strong>{rejectLinkKid?.full_name}</strong>. The parent will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                value={linkRejectionReason}
                onChange={(e) => setLinkRejectionReason(e.target.value)}
                placeholder="e.g., We do not recognise this parent."
                rows={3}
                data-testid="reject-link-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectLinkKid(null); setLinkRejectionReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectLink} data-testid="confirm-reject-link-btn">
              <UserMinus className="w-4 h-4 mr-2" /> Reject Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Kid Dialog */}
      <Dialog open={!!viewKid} onOpenChange={(open) => { if (!open) setViewKid(null); }}>
        <DialogContent className="max-w-lg" data-testid="view-kid-dialog">
          <DialogHeader>
            <DialogTitle>Kid Details</DialogTitle>
          </DialogHeader>
          {viewKid && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Full Name</Label>
                  <p className="text-sm font-medium">{viewKid.full_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Grade</Label>
                  <p className="text-sm font-medium">{viewKid.grade}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">School</Label>
                  <p className="text-sm font-medium">{viewKid.school_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Parent</Label>
                  <p className="text-sm font-medium">{viewKid.parent_name || 'Not linked yet'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Pickup Address (Morning)</Label>
                  <p className="text-sm font-medium">{viewKid.pickup_address}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Drop-off Address (Afternoon)</Label>
                  <p className="text-sm font-medium">{viewKid.drop_off_address || viewKid.pickup_address}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Kid ID (Share with parent)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 bg-slate-100 p-2 rounded text-xs break-all">{viewKid.kid_id}</code>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(viewKid.kid_id);
                        toast.success('Kid ID copied!');
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge className="mt-1" variant={viewKid.status === 'active' ? 'default' : 'secondary'}>{viewKid.status}</Badge>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Trip Dialog */}
      <Dialog open={!!viewTrip} onOpenChange={(open) => { if (!open) setViewTrip(null); }}>
        <DialogContent className="max-w-lg" data-testid="view-trip-dialog">
          <DialogHeader>
            <DialogTitle>Trip Details</DialogTitle>
          </DialogHeader>
          {viewTrip && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Route Name</Label>
                  <p className="text-sm font-medium">{viewTrip.route_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Trip Type</Label>
                  <p className="text-sm font-medium">{viewTrip.trip_type === 'morning' ? 'Morning (Home → School)' : 'Afternoon (School → Home)'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">School</Label>
                  <p className="text-sm font-medium">{viewTrip.school_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Term</Label>
                  <p className="text-sm font-medium">{viewTrip.term}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Start Date</Label>
                  <p className="text-sm font-medium">{viewTrip.start_date}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">End Date</Label>
                  <p className="text-sm font-medium">{viewTrip.end_date}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Scheduled Time</Label>
                  <p className="text-sm font-medium">{viewTrip.scheduled_time}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Kids on Trip</Label>
                  <p className="text-sm font-medium">{viewTrip.kid_ids?.length || 0} children</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge className="mt-1" variant={viewTrip.status === 'active' ? 'default' : 'secondary'}>{viewTrip.status}</Badge>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
